<template>
  <div class="w-100">
    <proa-card v-if="project" class="h-100 d-flex flex-column" no-padding>
      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center">
          <proa-back-button
            @onClick="onClickBackButton"
          />
          <proa-heading class="project-name" detail>{{ project.projectName }}</proa-heading>
          <div class="d-flex align-items-center">
            <div class="project-item" @click="projectInformationModal = true">
              <img :src="require('@/assets/images/icons/information.png')" />
            </div>
            <div class="project-item" @click="projectUsersModal = true">
              <img :src="require('@/assets/images/icons/user.png')" />
            </div>
            <div
              v-if="enabledProjectEditMenu"
              v-click-outside="() => { displayingProjectEditMenu = false }"
              class="project-item"
              @click="displayingProjectEditMenu = !displayingProjectEditMenu"
            >
              <img :src="require('@/assets/images/icons/ellipsis.png')" />
              <div
                v-show="displayingProjectEditMenu"
                class="project-edit-menu"
              >
                <div
                  @click="$router.push({ name: 'editProject', params: { id: projectId } })"
                  class="menu"
                >
                  {{ $t('pages.project.show.editProject') }}
                </div>
                <div
                  @click="deleteProjectModal = true"
                  class="menu"
                >
                  {{ $t('pages.project.show.deleteProject') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <project-menu-tab v-if="initialized" :defaultMenu="defaultMenu"/>
    </proa-card>
    <proa-modal
      v-if="project"
      :modal="projectInformationModal"
      :title="$t('common.detail')"
      @close="projectInformationModal = false"
    >
      <proa-heading sub>{{ project.projectName }}</proa-heading>
      <div
        v-if="project.projectDescription"
        v-html="$options.filters.autoLink(project.projectDescription)"
        style="margin-top: 20px; white-space: pre-wrap;"
      />
    </proa-modal>
    <proa-modal
      v-if="project"
      :modal="projectUsersModal"
      :title="`${$t('common.menus.member')}（${project.users.length}${$t('common.person')}）`"
      @close="projectUsersModal = false"
    >
      <div class="d-flex align-items-center" v-for="user in project.users" :key="user.id" style="margin-bottom: 10px;">
        <proa-avatar
          size="sm"
          :src="user.meta.avatar || require('@/assets/images/avatar_default.png')"
        />
        <span class="displayName">
          <span>{{ user.meta.displayName }}</span>
          <span v-if="project.projectOwnerId == user.id" class="owner">オーナー</span>
        </span>
      </div>
    </proa-modal>
    <proa-modal
      v-if="project"
      :modal="deleteProjectModal"
      :title="$t('common.confirm')"
      @close="() => { if (!deleteProjectRequesting) { deleteProjectModal = false } }"
    >
      <proa-heading sub>{{ $t('common.modals.deleteProjectModal.confirmMessage') }}</proa-heading>
      <template v-slot:footer>
        <proa-button
          @click.native="deleteProjectModal = null"
          :disabled="deleteProjectRequesting"
          class="mr-2"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          @click.native="onConfirmedDeleteProject"
          :loading="deleteProjectRequesting"
          variant="secondary"
        >
          {{ $t('common.delete') }}
        </proa-button>
      </template>
    </proa-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import camelCase from 'camelcase'
import ClickOutside from 'vue-click-outside'
import Project from '@/store/models/Project'

import ProjectMenuTab from '@/components/organisms/ProjectMenuTab'

export default {
  name: 'ShowProject',
  directives: {
    ClickOutside
  },
  components: {
    ProjectMenuTab
  },
  computed: {
    projectId: function() { return this.$route.params.id },
    project: function() {
      return Project.find(this.projectId)
    },
    defaultMenu: function() {
      return this.$route.params.defaultMenu ?? camelCase(String(this.$route.query.default_menu))
    },
    enabledProjectEditMenu: function() {
      return !this.project.projectOwnerId || this.project.projectOwnerId == this.signedInUser.id
    },
    ...mapGetters({
      httpError: 'httpError',
      signedInUser: 'auth/signedInUser'
    })
  },
  data() {
    return {
      initialized: false,
      projectInformationModal: false,
      projectUsersModal: false,
      deleteProjectModal: false,
      displayingProjectEditMenu: false,
      deleteProjectRequesting: false
    }
  },
  methods: {
    onClickBackButton: function() {
      this.$router.push({ name: 'project' })
    },
    async onConfirmedDeleteProject() {
      this.deleteProjectRequesting = true
      await this.apiDeleteProject(this.projectId)
      this.deleteProjectRequesting = this.deleteProjectModal = false
      if (!this.httpError) this.onClickBackButton()
    },
    ...mapActions({
      apiGetProject: 'entities/projects/show',
      apiGetTaskLists: 'entities/taskLists/index',
      apiGetTaskCards: 'entities/taskCards/index',
      apiDeleteProject: 'entities/projects/destroy',
      apiPutUserNotificationToRead: 'entities/userNotifications/updateToRead'
    })
  },
  async created() {
    if (this.project) {
      this.apiGetProject(this.projectId)
    } else {
      await this.apiGetProject(this.projectId)
      if (!this.project) {
        return
      }
    }
    await this.apiGetTaskLists(this.projectId)
    await this.apiGetTaskCards(this.projectId)
    this.initialized = true

    if (this.project.userNotificationId) this.apiPutUserNotificationToRead(this.project.userNotificationId)
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.project-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-basis: 60%;
  text-align: center;
}

.project-item {
  position: relative;
  cursor: pointer;
  width: 26px;
  height: 26px;
  padding: 3px;
  margin-left: 10px;
  border-radius: 4px;
  display: flex;

  &:hover {
    background-color: $background-color;
  }

  img {
    width: 100%;
    height: 100%;
  }
}

.project-edit-menu {
  position: absolute;
  z-index: 2;
  top: 40px;
  right: 0;
  width: 250px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0px 0px 4px #00000026;

  .menu {
    cursor: pointer;
    padding: 10px;
    font-size: 13px;

    &:hover {
      background-color: $light-gray-color;
    }
  }
}
</style>
